import React, { useState, useEffect, useContext } from 'react';
import { getJustificationsList, getJustificationsCompanyList, addJustificationCompany, deleteJustificationCompany, updateJustificationCompany } from '../services/api_justifications';
import { getCompanySettings, updateCompanySettings, } from '../services/api_settings';
import { AuthContext } from '../contexts/auth';
import { ArrowDownIcon, ArrowUpIcon, PauseIcon, CheckCircleIcon, XCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import CustomTooltipComponent from './custom_tooltip_component';

const GiustificativiTable = () => {
  const [giustificativi, setGiustificativi] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { loading, token } = useContext(AuthContext);
  const [companyJustifications, setCompanyJustifications] = useState([]);
  const [isNightShiftActive, setIsNightShiftActive] = useState(false);
  const [editReferenceId, setEditReferenceId] = useState(null);
  const [editedReference, setEditedReference] = useState('');

  useEffect(() => {
    const fetchGiustificativi = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const giustificativiData = await getJustificationsList(token);
        giustificativiData.sort((a, b) => a.code.localeCompare(b.code));
        setGiustificativi(giustificativiData);
      } catch (error) {
        setError(new Error(String(error)));
      } finally {
        setIsLoading(false);
      }
    };
    fetchGiustificativi();
  }, [token]);

  useEffect(() => {
    const fetchCompanyJustifications = async () => {
      try {
        const updatedJustifications = await getJustificationsCompanyList(token);
        setCompanyJustifications(updatedJustifications);

      } catch (error) {
        console.error('Error fetching company justifications:', error);
      }
    };

    fetchCompanyJustifications();
  }, [token]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getCompanySettings(token, 'attendance');

        setIsNightShiftActive(settings?.night_shift_active || false);
      } catch (err) {
        setError("Errore durante il caricamento delle impostazioni.");
        console.error(err);
      }
    };

    fetchSettings();
  }, [token]); 

  const handleEnableJustification = async (justificationId) => {
    try {
      if (companyJustifications.find(j => j.justification.id === justificationId)) {
        throw new Error('Giustificativo già abilitato per l\'azienda');
      }
  
      await addJustificationCompany(token, justificationId);
  
      const updatedCompanyJustifications = await getJustificationsCompanyList(token);
      setCompanyJustifications(updatedCompanyJustifications);
    } catch (error) {
      console.error('Errore durante l\'abilitazione della giustificazione:', error);
    }
  };
  
  const handleDisableJustification = async (justificationId, justificationCode) => {
    if (justificationCode === 'LN' && isNightShiftActive) {
      return Swal.fire({
        html: `
          <p>È attiva l'<strong>assegnazione automatica</strong> del giustificativo Lavoro Notturno.</br> 
          Per disabilitare il giustificativo è prima necessario disabilitare l'assegnazione automatica.</br> 
          Vuoi procedere?</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Disabilita Assegnazione Automatica LN',
        cancelButtonText: 'Annulla'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await updateCompanySettings(token, { night_shift_active: false });

            setIsNightShiftActive(false);
            Swal.fire({
              icon: "success",
              title: "Assegnazione automatica disabilitata!",
              showConfirmButton: true,
              confirmButtonText: "Disabilita Giustificativo",
              cancelButtonText: "Chiudi",
              showCancelButton: true
          }).then((res) => {
            if (res.isConfirmed) {
              disableJustification(justificationId);
            }
          });
        } catch (error) {
          console.error("Errore durante la disabilitazione dell'assegnazione automatica:", error);

          Swal.fire({
            icon: "error",
            title: "Errore",
            text: "Non è stato possibile disabilitare l'assegnazione automatica. Per favore riprova.",
            confirmButtonText: "OK",
          });
        }
      }
      });
    }
    disableJustification(justificationId);
  };

  const disableJustification = async (justificationId) => {
    Swal.fire({
      html: `
      <p>Disabilitando il giustificativo verranno eliminate tutte le richieste di giustificativo elaborate e le richieste in approvazione esistenti ad esso collegate. Questa azione non può essere annullata!</p>
      <p>Digita <strong>\"ELIMINA\"</strong> per confermare</p>
      `,
      icon: 'warning',
      input: 'text',
      inputPlaceholder: 'Digita ELIMINA',
      inputValidator: (value) => {
        if (value !== 'ELIMINA') {
          return 'Digitare "ELIMINA" per procedere';
        }
        return null;
      },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sì, elimina!',
      cancelButtonText: 'Annulla'
    }).then(async (result) => {
      if (result.isConfirmed && result.value === 'ELIMINA') {
        try {
          const justificationToDisable = companyJustifications.find(j => j.justification.id === justificationId);
          if (!justificationToDisable) {
            throw new Error('Justification not enabled for the company');
          }
  
          const idAsString = String(justificationToDisable.id);
          await deleteJustificationCompany(token, idAsString);
  
          const updatedCompanyJustifications = companyJustifications.filter(j => j.id !== justificationToDisable.id);
          setCompanyJustifications(updatedCompanyJustifications);
  
          Swal.fire({
            position: "center",
            icon: "success",
            title: "Disabilitato!",
            showConfirmButton: false,
            timer: 1500
          });
        } catch (error) {
          console.error('Errore durante la disabilitazione del giustificativo:', error);
          Swal.fire({
            title: 'Errore!',
            text: 'Si è verificato un errore durante la disabilitazione del giustificativo.',
            icon: 'error'
          });
        }
      }
    });
  };

  const handleSaveReference = async (justificationId) => {
    try {
      const companyJ = companyJustifications.find(j => j.justification.id === justificationId);
      if (!companyJ) throw new Error("Giustificativo non abilitato per l'azienda");

      await updateJustificationCompany(token, companyJ.id, editedReference);

      const updated = await getJustificationsCompanyList(token);
      setCompanyJustifications(updated);
      setEditReferenceId(null);
      setEditedReference('');
    } catch (error) {
      console.error("Errore nel salvataggio dell'external_reference:", error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: error.message || "Non è stato possibile aggiornare l'ID esterno.",
      });
    }
  };
  
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <fieldset className='min-w-full overflow-x-auto'>
        <div className="mt-5 mx-10 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th colSpan={1} className="px-4 pl-10 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Codice</th>
                <th colSpan={2} className="px-4 pl-10 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Descrizione</th>
                <th colSpan={2} className="px-4 pl-10 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-normal">Azione sulle Ore lavorate</th>
                <CustomTooltipComponent title='ID conversione per gestionale esterno' placement="top" arrow>
                  <th colSpan={2} className="px-4 pl-10 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-normal">Id Esterno</th>
                </CustomTooltipComponent>
                <th colSpan={1} className="px-4 pl-10 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-normal">Stato</th>
                <th colSpan={1} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-normal">Abilita/Disabilita</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {giustificativi.map((giustificativo, giustificativoIdx) => (
                <tr key={giustificativoIdx}>
                  <td colSpan={1} className="px-4 pl-10 whitespace-nowrap text-gray-500 text-sm">{giustificativo.code}</td>
                  <td colSpan={2} className="px-4 pl-10 whitespace-nowrap text-gray-500 text-sm">{giustificativo.description}</td>
                  <td colSpan={2} className="px-4 pl-10 whitespace-nowrap text-gray-500 text-sm">
                    {giustificativo.strategy === "increase" ? (
                      <>
                        <ArrowUpIcon className="inline-block h-3 w-3 text-indigo-600" /> Aumenta
                      </>
                    ) : giustificativo.strategy === "decrease" ? (
                      <>
                        <ArrowDownIcon className="inline-block h-3 w-3 text-gray-900" /> Riduce
                      </>
                    ) : (
                      <>
                        <PauseIcon className="inline-block h-3 w-3 text-gray-900" /> Nessuna azione
                      </>
                    )}
                  </td>
                  <td colSpan={2} className="text-center whitespace-nowrap text-gray-500 text-sm">
                    {(() => {
                      const companyJ = companyJustifications.find(j => j.justification.id === giustificativo.id);

                      if (!companyJ) {
                        return '';
                      }

                      return editReferenceId === giustificativo.id ? (
                        <div className="flex items-center gap-2 justify-center]">
                          <input
                            type="text"
                            className="border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded px-2 py-1 w-[100px] text-sm"
                            value={editedReference}
                            onChange={(e) => setEditedReference(e.target.value)}
                            placeholder="ID esterno"
                          />
                          <button
                            onClick={() => handleSaveReference(giustificativo.id)}
                            className="text-indigo-600 text-xs font-semibold hover:underline"
                          >
                            Salva
                          </button>
                          <button
                            onClick={() => {
                              setEditReferenceId(null);
                              setEditedReference('');
                            }}
                            className="text-gray-500 text-xs hover:underline"
                          >
                            Annulla
                          </button>
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer hover:text-indigo-700"
                          onClick={() => {
                            setEditReferenceId(giustificativo.id);
                            setEditedReference(companyJ.external_reference || '');
                          }}
                        >
                          {companyJ.external_reference ? (
                            companyJ.external_reference
                          ) : (
                            <span className="text-gray-400 italic">
                              clicca per inserire
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {companyJustifications.find(companyJustification => companyJustification.justification.id === giustificativo.id) ? (
                    <>
                      <td colSpan={1} className="px-4 pl-6 whitespace-nowrap text-gray-500 text-sm">
                        <span className='bg-indigo-100 rounded-md p-2 ml-3'><CheckCircleIcon className="inline-block h-5 w-5 text-indigo-600" /> ON</span>
                      </td>
                      <td colSpan={1} className="px-4 py-2 pl-10 whitespace-nowrap text-gray-500 text-sm justify-center flex">
                        <button
                          onClick={() => giustificativo.id !== undefined && handleDisableJustification(giustificativo.id, giustificativo.code)}
                          className="border flex items-center rounded-md bg-grey-400 px-3 py-1 text-center text-xs font-semibold text-gray-900 shadow-sm hover:bg-gray-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-28 justify-center"
                        >
                          Disabilita
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td colSpan={1} className="px-4 pl-6 whitespace-nowrap text-gray-500 text-sm">
                        <span className='p-2 ml-3'><XCircleIcon className="inline-block h-5 w-5 text-gray-500" /> OFF</span>
                      </td>
                      <td colSpan={1} className="px-4 py-2 pl-10 whitespace-nowrap text-gray-500 text-sm justify-center flex">
                        <button
                          onClick={() => giustificativo.id !== undefined && handleEnableJustification(giustificativo.id)}
                          className="border flex items-center rounded-md bg-grey-400 px-3 py-1 text-center text-xs font-semibold text-gray-900 shadow-sm hover:bg-indigo-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-28 justify-center"
                        >
                          Abilita
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>
    </>
  );
};

export default GiustificativiTable;
